<template>
  <div :class="containerClass">
    <!-- Label -->
    <label
      v-if="label"
      :for="inputId"
      class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
    >
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>

    <!-- Password Input with Toggle -->
    <div v-if="type === 'password' && showPasswordToggle" class="relative">
      <input
        :id="inputId"
        :type="showPasswordValue ? 'text' : 'password'"
        :value="modelValue"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :readonly="readonly"
        :class="inputClasses"
        @input="handleInput"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
      />
      <button
        type="button"
        class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
        @click="showPasswordValue = !showPasswordValue"
      >
        <svg v-if="!showPasswordValue" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.956 9.956 0 012.293-3.95m3.36-2.676A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.956 9.956 0 01-4.043 5.306M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3l18 18" />
        </svg>
      </button>
    </div>

    <!-- Regular Input or Select or Textarea -->
    <component
      v-else
      :is="componentType"
      :id="inputId"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :readonly="readonly"
      :min="min"
      :max="max"
      :step="step"
      :rows="rows"
      :class="inputClasses"
      @input="handleInput"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    >
      <!-- Options for select -->
      <template v-if="componentType === 'select'">
        <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
        <option
          v-for="option in options"
          :key="getOptionValue(option)"
          :value="getOptionValue(option)"
        >
          {{ getOptionLabel(option) }}
        </option>
      </template>
    </component>

    <!-- Hint text -->
    <p v-if="hint && !error" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
      {{ hint }}
    </p>

    <!-- Error message -->
    <p v-if="error" class="mt-1 text-xs text-red-500 dark:text-red-400">
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  type: {
    type: String,
    default: 'text',
    validator: (value) => ['text', 'email', 'password', 'tel', 'number', 'date', 'datetime-local', 'time', 'url', 'search'].includes(value)
  },
  inputType: {
    type: String,
    default: 'input',
    validator: (value) => ['input', 'select', 'textarea'].includes(value)
  },
  variant: {
    type: String,
    default: 'standard',
    validator: (value) => ['standard', 'filter', 'compact'].includes(value)
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  hint: {
    type: String,
    default: ''
  },
  error: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  fullWidth: {
    type: Boolean,
    default: true
  },
  showPasswordToggle: {
    type: Boolean,
    default: false
  },
  // Number inputs
  min: {
    type: [Number, String],
    default: undefined
  },
  max: {
    type: [Number, String],
    default: undefined
  },
  step: {
    type: [Number, String],
    default: undefined
  },
  // Textarea
  rows: {
    type: [Number, String],
    default: 3
  },
  // Select
  options: {
    type: Array,
    default: () => []
  },
  optionValue: {
    type: String,
    default: 'value'
  },
  optionLabel: {
    type: String,
    default: 'label'
  }
})

const showPasswordValue = ref(false)

const emit = defineEmits(['update:modelValue', 'input', 'blur', 'focus'])

const inputId = computed(() => `input-${Math.random().toString(36).substr(2, 9)}`)

const componentType = computed(() => props.inputType)

const handleInput = (event) => {
  const value = event.target.value
  emit('update:modelValue', value)
  emit('input', value)
}

const getOptionValue = (option) => {
  return typeof option === 'object' ? option[props.optionValue] : option
}

const getOptionLabel = (option) => {
  return typeof option === 'object' ? option[props.optionLabel] : option
}

const containerClass = computed(() => props.fullWidth ? 'w-full' : '')

const baseInputClasses = 'border rounded focus:outline-none transition-colors duration-200'

const sizeClasses = computed(() => {
  const sizes = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  }
  return sizes[props.size]
})

const variantClasses = computed(() => {
  const variants = {
    standard: 'border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white',
    filter: 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800',
    compact: 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:ring-1 focus:ring-violet-200 dark:focus:ring-violet-800'
  }
  return variants[props.variant]
})

const stateClasses = computed(() => {
  if (props.error) return 'border-red-500 focus:ring-red-500'
  if (props.readonly || props.disabled) return 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
  return ''
})

const widthClass = computed(() => props.fullWidth ? 'w-full' : '')

const inputClasses = computed(() => [
  baseInputClasses,
  sizeClasses.value,
  variantClasses.value,
  stateClasses.value,
  widthClass.value
])
</script>
